import React, { useMemo, useRef, useState } from 'react';
import { Box, IconButton, Paper, Tooltip, Typography, useTheme } from '@mui/material';
import {
  Close as CloseIcon,
  ContentCopy as CopyIcon,
  Download as DownloadIcon,
  Videocam as CameraIcon,
  AccessTime as DwellIcon,
  Pin as BadgesIcon,
  UnfoldLess as CollapseIcon,
  UnfoldMore as ExpandIcon,
  DragIndicator as DragIcon,
} from '@mui/icons-material';
import {
  downloadTextFile,
  routeSequenceFilename,
  routeSequenceToCsv,
  routeSequenceToTsv,
  routeSequenceEntryColor,
  type RouteSequenceEntry,
  type RouteSequenceGroup,
} from '@signals/app-shell/services/routeSequence';
import { INTEL_MONO } from '@signals/app-shell/components/map/IntelPopup';

type Props = {
  groups: RouteSequenceGroup[];
  radiusM: number;
  camerasIncluded: boolean;
  dwellsIncluded: boolean;
  showBadges: boolean;
  onToggleBadges: () => void;
  onClose: () => void;
  onSelect?: (entry: RouteSequenceEntry) => void;
  timeZone?: string;
  /** Offsets so the panel clears other map furniture. */
  top?: number;
  left?: number;
};

// Seconds matter: several cameras and a dwell often share the same minute.
const fmtClock = (d: Date, tz?: string) => {
  try {
    return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false, timeZone: tz });
  } catch {
    return d.toLocaleTimeString();
  }
};
const fmtDay = (d: Date, tz?: string) => {
  try {
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: tz });
  } catch {
    return d.toLocaleDateString();
  }
};

/**
 * Abbreviated, toggleable overlay listing the cameras passed and places
 * dwelled along the loaded route, in order, with CSV export / copy.
 */
const RouteSequencePanel: React.FC<Props> = ({
  groups,
  radiusM,
  camerasIncluded,
  dwellsIncluded,
  showBadges,
  onToggleBadges,
  onClose,
  onSelect,
  timeZone,
  top = 60,
  left = 10,
}) => {
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';
  const [collapsed, setCollapsed] = useState(false);
  const [copied, setCopied] = useState(false);

  // Drag anywhere on the header (not its buttons) to move the panel around the
  // map; position is kept in map-container pixels and clamped to stay visible.
  const paperRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ x: number; y: number }>({ x: left, y: top });
  const dragRef = useRef<{ startX: number; startY: number; x: number; y: number } | null>(null);
  const clamp = (x: number, y: number) => {
    const el = paperRef.current;
    const host = el?.offsetParent as HTMLElement | null;
    if (!el || !host) return { x, y };
    const maxX = Math.max(0, host.clientWidth - el.offsetWidth);
    const maxY = Math.max(0, host.clientHeight - 40);
    return { x: Math.min(Math.max(0, x), maxX), y: Math.min(Math.max(0, y), maxY) };
  };
  const onHeaderPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    if ((e.target as HTMLElement).closest('button')) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    dragRef.current = { startX: e.clientX, startY: e.clientY, x: pos.x, y: pos.y };
  };
  const onHeaderPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = dragRef.current;
    if (!d) return;
    setPos(clamp(d.x + (e.clientX - d.startX), d.y + (e.clientY - d.startY)));
  };
  const onHeaderPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    dragRef.current = null;
  };

  const total = useMemo(() => groups.reduce((n, g) => n + g.entries.length, 0), [groups]);
  const cams = useMemo(() => groups.reduce((n, g) => n + g.entries.filter((e) => e.kind === 'camera').length, 0), [groups]);
  const dwells = total - cams;

  const exportCsv = () => downloadTextFile(routeSequenceFilename(groups), routeSequenceToCsv(groups, timeZone));
  const copyTable = async () => {
    try {
      await navigator.clipboard.writeText(routeSequenceToTsv(groups, timeZone));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable */
    }
  };

  const scope = [camerasIncluded ? `cameras ≤ ${radiusM} m` : null, dwellsIncluded ? 'dwells' : null].filter(Boolean).join(' · ');

  return (
    <Paper
      ref={paperRef}
      elevation={6}
      data-route-sequence=""
      sx={{
        position: 'absolute',
        top: pos.y,
        left: pos.x,
        zIndex: 3,
        width: 306,
        maxHeight: 'calc(100% - 120px)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderRadius: 1,
        bgcolor: dark ? 'rgba(18,18,18,0.92)' : 'rgba(255,255,255,0.95)',
        backdropFilter: 'blur(6px)',
        border: `1px solid ${dark ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.12)'}`,
      }}
      onWheel={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
    >
      <Box
        onPointerDown={onHeaderPointerDown}
        onPointerMove={onHeaderPointerMove}
        onPointerUp={onHeaderPointerUp}
        onPointerCancel={onHeaderPointerUp}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          px: 0.5,
          py: 0.5,
          borderBottom: `1px solid ${theme.palette.divider}`,
          cursor: 'move',
          touchAction: 'none',
          userSelect: 'none',
        }}
        title="Drag to move"
      >
        <DragIcon sx={{ fontSize: 16, color: 'text.disabled', flexShrink: 0 }} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, lineHeight: 1.2 }}>
            Route sequence <Typography component="span" sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>({total})</Typography>
          </Typography>
          <Typography sx={{ fontSize: '0.62rem', color: 'text.secondary', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {cams} camera{cams === 1 ? '' : 's'} · {dwells} dwell{dwells === 1 ? '' : 's'}{scope ? ` · ${scope}` : ''}
          </Typography>
        </Box>
        <Tooltip title={showBadges ? 'Hide numbers on map' : 'Show numbers on map'}>
          <IconButton size="small" onClick={onToggleBadges} color={showBadges ? 'primary' : 'default'} aria-pressed={showBadges} aria-label="Toggle sequence numbers on map">
            <BadgesIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title={copied ? 'Copied' : 'Copy table (tab-separated)'}>
          <span>
            <IconButton size="small" onClick={copyTable} disabled={total === 0} aria-label="Copy route sequence table">
              <CopyIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Export CSV">
          <span>
            <IconButton size="small" onClick={exportCsv} disabled={total === 0} aria-label="Export route sequence CSV">
              <DownloadIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={collapsed ? 'Expand list' : 'Collapse list'}>
          <IconButton size="small" onClick={() => setCollapsed((v) => !v)} aria-label="Collapse route sequence list">
            {collapsed ? <ExpandIcon sx={{ fontSize: 16 }} /> : <CollapseIcon sx={{ fontSize: 16 }} />}
          </IconButton>
        </Tooltip>
        <Tooltip title="Close">
          <IconButton size="small" onClick={onClose} aria-label="Close route sequence">
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {!collapsed && (
        <Box sx={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
          {total === 0 ? (
            <Typography sx={{ p: 1.25, fontSize: '0.72rem', color: 'text.secondary' }}>
              {!camerasIncluded && !dwellsIncluded
                ? 'Turn on Camera Locations and/or Dwells to build the sequence.'
                : 'No cameras within range and no dwells along the loaded track.'}
            </Typography>
          ) : (
            groups.map((g) => (
              <Box key={g.entityId}>
                {groups.length > 1 && (
                  <Typography
                    sx={{
                      px: 1,
                      py: 0.25,
                      fontSize: '0.65rem',
                      fontWeight: 700,
                      letterSpacing: 0.4,
                      textTransform: 'uppercase',
                      color: 'text.secondary',
                      bgcolor: dark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
                      position: 'sticky',
                      top: 0,
                    }}
                  >
                    {g.entityId} · {g.entries.length}
                  </Typography>
                )}
                {g.entries.map((e, i) => {
                  const color = routeSequenceEntryColor(e);
                  const prev = i > 0 ? g.entries[i - 1] : null;
                  const newDay = !prev || fmtDay(prev.time, timeZone) !== fmtDay(e.time, timeZone);
                  return (
                    <Box
                      key={`${e.kind}-${e.cameraId ?? ''}-${e.time.getTime()}-${e.seq}`}
                      role={onSelect ? 'button' : undefined}
                      tabIndex={onSelect ? 0 : undefined}
                      onClick={() => onSelect?.(e)}
                      onKeyDown={(ev) => {
                        if (onSelect && (ev.key === 'Enter' || ev.key === ' ')) {
                          ev.preventDefault();
                          onSelect(e);
                        }
                      }}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '22px 56px 16px 1fr',
                        alignItems: 'center',
                        columnGap: 0.6,
                        px: 1,
                        py: 0.4,
                        cursor: onSelect ? 'pointer' : 'default',
                        borderTop: newDay && i > 0 ? `1px dashed ${theme.palette.divider}` : 'none',
                        '&:hover': onSelect ? { bgcolor: dark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)' } : undefined,
                      }}
                      title={`${e.label}\n${e.detail}\n${e.time.toLocaleString()} → ${e.endTime.toLocaleString()}${e.address ? `\n${e.address}` : ''}`}
                    >
                      <Box
                        sx={{
                          width: 18,
                          height: 18,
                          borderRadius: '50%',
                          bgcolor: color,
                          color: '#fff',
                          fontSize: '0.6rem',
                          fontWeight: 800,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 0 0 1.5px #fff, 0 0 0 2.5px rgba(0,0,0,0.5)',
                        }}
                      >
                        {e.seq}
                      </Box>
                      <Box sx={{ lineHeight: 1.1 }}>
                        <Typography sx={{ fontFamily: INTEL_MONO, fontSize: '0.68rem', fontWeight: 600 }}>{fmtClock(e.time, timeZone)}</Typography>
                        {newDay && (
                          <Typography sx={{ fontSize: '0.55rem', color: 'text.secondary' }}>{fmtDay(e.time, timeZone)}</Typography>
                        )}
                      </Box>
                      {e.kind === 'camera' ? (
                        <CameraIcon sx={{ fontSize: 14, color }} />
                      ) : (
                        <DwellIcon sx={{ fontSize: 14, color }} />
                      )}
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, lineHeight: 1.15, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {e.label}
                        </Typography>
                        <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary', lineHeight: 1.15, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {e.detail}
                          {e.kind === 'dwell' ? ` · until ${fmtClock(e.endTime, timeZone)}` : e.hits && e.hits > 1 ? ` · ${e.hits} pts` : ''}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            ))
          )}
        </Box>
      )}
    </Paper>
  );
};

export default RouteSequencePanel;
